import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { first, Observable, ReplaySubject } from 'rxjs';
import environment from 'src/environments/environment';
import { SocialAuthService } from '@abacritt/angularx-social-login';
import { Router } from '@angular/router';
import {
  ApiAddResponse,
  ApiSuccessfulLogin,
  InsuranceProductsResponse,
  UserInsuranceProduct,
} from '../interfaces/api';
import { User } from '../shared/models/user.model';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public errorMsg = '';

  public currentUser: User | null | undefined;

  private currentUserSubject = new ReplaySubject<User>(1);

  public currentUserToken: string | null | undefined;

  // a place for the social user, if that's how they're logging in, to be stored if needed i.e. for registration
  public socialUser: User | null | undefined;

  public csrfLoaded = false;

  public logoutReason = 0;

  public redirectAfterLogin: string | null = null;

  public migratedUserMustReset = false;

  public userMustCompleteProfile = false;

  public referrer = '';

  public hasInsuranceProducts: boolean = false;

  public userCoverSet: boolean = false;

  public initialDataFetched: boolean = false;

  public hasStudentGuardInsurance: boolean = false;

  // Lazy-loaded SocialAuthService to avoid circular dependency
  private socialAuthServiceInstance: SocialAuthService | null = null;

  private get socAuth(): SocialAuthService | null {
    if (!this.socialAuthServiceInstance) {
      try {
        this.socialAuthServiceInstance = this.injector.get(SocialAuthService);
      } catch {
        // Ignored
      }
    }
    return this.socialAuthServiceInstance;
  }

  constructor(
    private readonly http: HttpClient,
    private readonly injector: Injector,
    private router: Router,
    private readonly api: ApiService
  ) {}

  public getCurrentUser(): Observable<User> {
    return this.currentUserSubject.asObservable();
  }

  public populateUserSubject: Observable<User> = this.getCurrentUser().pipe(
    first((user: User) => !!user?.uuid)
  );

  public emailLogin(username: string, password: string) {
    // reset the logout reason, just in case
    this.logoutReason = 0;

    const endpoint = `${this.api.url}/auth/login/web`;

    return this.http.post(endpoint, { username, password });
  }

  /**
   * A service used to login via the admin reports
   * @param username string
   * @param password stinrg
   * @returns
   */
  public reportEmailLogin(username: string, password: string) {
    const endpoint = `${this.api.url}/auth/login/reports`;

    return this.http.post(endpoint, { username, password });
  }

  // placeholder to manually test guards etc.
  // will be replaced with correct check of session / social sign in stuff
  public isAuthenticated(): boolean {
    return !!this.currentUser;
  }

  /**
   * Checks if there is a valid session
   * Returns true / false
   *
   */
  public checkUserAuth(): Observable<{ authenticated: boolean }> {
    const endpoint = `${this.api.url}/auth/check-user-auth`;
    return this.http.get<{ authenticated: boolean }>(endpoint);
  }

  public resetPassword(
    password: string,
    uuid: string,
    token: string
  ): Observable<ApiAddResponse> {
    const endpoint = `${this.api.url}/auth/${uuid}/reset-password`;
    return this.http.post<ApiAddResponse>(endpoint, { password, token });
  }

  public login(response: ApiSuccessfulLogin): void {
    this.currentUser = response.data.user;
    this.logoutReason = 0;
    this.referrer = '';
    this.setUserInSession();
    this.afterUserUpdate();
  }

  /**
   * function to handle updating the current user object after it has been updated
   * generally this will be because of a uni/accommodation change that may affect
   * whether they have content cover or not
   */
  public afterUserUpdate(): void {
    // the user returned from the api will not have coverCount and insuranceProducts properties
    // so call setUserInsuranceProducts which will restore both
    this.setUserInsuranceProducts();

    if (this.currentUser) {
      this.currentUserSubject.next(this.currentUser);
    }
  }

  /**
   * Fetches and set the authenticated users insurance products and then
   * sets the users CoverCount
   * @returns void
   */
  private setUserInsuranceProducts(): void {
    this.getUserInsuranceProducts().subscribe({
      next: (productResponse: InsuranceProductsResponse) => {
        const products = productResponse.data;
        if (this.currentUser) this.currentUser.insuranceProducts = products;
      },
      error: () => {
        this.setUserInSession();
      },
      complete: () => {
        if (this.currentUser) {
          const coverCount = this.getCoverCount();

          this.checkForStudentGuard();

          if (+coverCount !== this.currentUser.coverCount) {
            this.currentUser.coverCount = coverCount;
          }
          this.hasInsuranceProducts = true;
          this.userCoverSet = true;
        }
        this.setUserInSession();
      },
    });
  }

  /**
   * Will check if the user has student guard insurance
   * @returns void
   */
  public checkForStudentGuard(): void {
    if (this.currentUser?.insuranceProducts?.length) {
      this.hasStudentGuardInsurance = !!this.currentUser.insuranceProducts.find(
        (product: UserInsuranceProduct) => {
          if (product.product.cmsUid === 'studentguard') return product;
          return null;
        }
      );
    }
  }

  public sanctumCSRF(): Observable<Object> {
    return this.http.get(`${environment.apiHost}/sanctum/csrf-cookie`);
  }

  public socialLogin(
    token: string,
    method: string,
    isUnihomesRegister: boolean = false
  ): Observable<Object> {
    // reset the logout reason, just in case
    this.logoutReason = 0;

    return this.http.post(`${this.api.url}/auth/login/social/web`, {
      token,
      method,
      isUnihomesRegister,
    });
  }

  public googleOAuthLogin({
    clientId,
    token,
  }: {
    clientId: string;
    token: string;
  }): Observable<Object> {
    return this.http.post(`${this.api.url}/oauth2/providers/google`, {
      token,
      clientId,
    });
  }

  public facebookOAuthLogin({
    clientId,
    token,
  }: {
    clientId: string;
    token: string;
  }): Observable<Object> {
    return this.http.post(`${this.api.url}/oauth2/providers/facebook`, {
      token,
      clientId,
    });
  }

  public clearSession(): void {
    this.currentUser = null;
    this.socialUser = null;
    localStorage.removeItem('my-endsleigh-logged-in');
    localStorage.removeItem('my-endsleigh-user');
    this.socAuth?.signOut().finally(() => false);
  }

  public logout(): void {
    this.clearSession();

    if (this.router.url.includes('/eis/')) {
      this.router.navigate(['/eis/login']).finally(() => false);
    } else {
      this.router.navigate(['']).finally(() => false);
    }
  }

  public checkReturningUser(): Promise<boolean> {

    return new Promise<boolean>((resolve) => {
      if (
        localStorage.getItem('my-endsleigh-logged-in') === 'yes' &&
        localStorage.getItem('my-endsleigh-user')
      ) {
        this.currentUser = <User>(
          JSON.parse(String(localStorage.getItem('my-endsleigh-user')))
        );

        this.checkUserAuth().subscribe((res) => {
          if (res.authenticated) this.afterUserUpdate();
        });
      }

      resolve(true);
    });
  }

  public setUserInSession(): void {
    localStorage.setItem('my-endsleigh-logged-in', 'yes');
    localStorage.setItem('my-endsleigh-user', JSON.stringify(this.currentUser));
    this.initialDataFetched = true;
  }

  public storeSocialUserInSession(): void {
    localStorage.setItem('registering-via-social', 'yes');
    localStorage.setItem(
      'registering-via-social-expires',
      String(new Date().getTime() + 600000)
    ); // expire after 10 mins, just in case
    localStorage.setItem('social-user', JSON.stringify(this.socialUser));
  }

  // for clearing out session, in case they click for email reg, or finish social, or social expires
  public clearSocialSession(): void {
    localStorage.removeItem('registering-via-social');
    localStorage.removeItem('registering-via-social-expires');
    localStorage.removeItem('social-user');
  }

  public loadSocialUserIfInSession(): void {
    const expiresAt = Number(
      localStorage.getItem('registering-via-social-expires')
    );

    if (
      localStorage.getItem('registering-via-social') === 'yes' &&
      localStorage.getItem('social-user') &&
      new Date().getTime() <= expiresAt
    ) {
      this.socialUser = <User>(
        JSON.parse(String(localStorage.getItem('social-user')))
      );
    } else {
      // probably expired. clean it up.
      this.clearSocialSession();
    }
  }

  public changePassword(oldPassword: string, newPassword: string) {
    const uuid = String(this.currentUser?.uuid);
    const endpoint = `${this.api.url}/auth/${uuid}/update-password`;
    return this.http.post(endpoint, { oldPassword, newPassword });
  }

  public getUserInsuranceProducts(): Observable<InsuranceProductsResponse> {
    const endpoint = `${this.api.url}/users/${String(
      this.currentUser?.uuid
    )}/insurance`;
    return this.http.get<InsuranceProductsResponse>(endpoint);
  }

  // gets the total number of insurance products the user has
  // either from insuranceProducts from external providers, variable number of policies
  // or from their accommodation (if provided), one policy only, regardless of # documents
  private getCoverCount(): number {
    let count = this.currentUser?.insuranceProducts?.length ?? 0;

    if (this.currentUser?.accommodation.covered) {
      count += 1;
    }

    if (this.currentUser?.university?.is_pathway_provider) {
      count += 1;
    }

    return count;
  }

  public setCoveredAndWellbeingError(): void {
    // only need to set this if the user's accom has a cms id
    // because we'll only get here if there was an error contacting Cms
    // if it doesn't then there's no way to tell if it's covered or not as it's probably a new accom
    // ditto for wellbeing
    if (this.currentUser?.accommodation?.cmsUid) {
      this.currentUser.coveredError = true;
      this.currentUser.wellbeingError = true;
    }
  }
}
